import React from 'react';

import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import NoteIcon from '@mui/icons-material/Note';
import TablePagination from '@mui/material/TablePagination';
import BackupTable from '@mui/icons-material/BackupTable';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ManageHistoryIcon from '@mui/icons-material/ManageHistory';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import ClearAll from '@mui/icons-material/ClearAll';

import { AppState } from '../states/appState';
import { RepealState } from '../states/repealState';
import { SearchState } from '../states/searchState';
import { RepealActions } from '../containers/repealContainer';
import { Hit, Conditions, EditingGroup } from '../states/searchState';
import { RevisionDialog } from './RevisionDialog'

type RepealProps = {} & AppState & RepealState & RepealActions & SearchState;

export const Repeal: React.FC<RepealProps> = (props) => {

  const actView = (actId, revision) => {
    const query = `?type=${'revision'}&actId=${actId}&miekesi=false`;
    const url = process.env.REACT_APP_VIEWER_URL ? process.env.REACT_APP_VIEWER_URL.replace('{hostname}', window.location.hostname) : process.env.REACT_APP_VIEWER_URL;
    window.open(url + query, `viewer_${actId}_${revision}`);
  };

  const pageChange = (event, page) => {
    const c: Conditions = JSON.parse(JSON.stringify(props.rconditions));
    c.page = page;
    props.setLoading(true);
    props.searchRepealAsync(c, props.accountid);
  };

  const defaultLabelDisplayedRows = ({ from, to, count }) => {
    return `${count !== -1 ? count : `more than ${to}`}件中 ${from}–${to}件目`;
  };

  // 一覧が長くなる為、上下どちらでも件数の確認とページ送りが出来るようにする
  const createTablePagination = () => {
    return (
      <TablePagination
        rowsPerPageOptions={[100]}
        component="div"
        count={props.rsearchResultSet ? props.rsearchResultSet.found : 0}
        rowsPerPage={props.rconditions.rowsPerPage}
        page={props.rconditions.page}
        onPageChange={pageChange}
        labelDisplayedRows={defaultLabelDisplayedRows}
      />
    );
  };

  // リビジョン操作ダイアログ表示
  const [anchorElRevisionDialog, setAnchorElRevisionDialog] = React.useState<null | HTMLElement>(null);
  const openRevisionDialog = Boolean(anchorElRevisionDialog);
  const handleRevisionDialog = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElRevisionDialog(event.currentTarget);
  };
  const handleCloseRevisionDialog = () => {
    props.clearEnforcegroups('');
    setAnchorElRevisionDialog(null);
  };
  const handleCommitRevisionDialog = (editingGroups: EditingGroup[]) => {
    // commit
    props.setLoading(true);
    //props.postEnforcegroups(props.accountid, props.id, props.idid, props.editingGroups[0].enforceGroups[0].actId, editType, editingGroups);
    const targetEId = editingGroups[editingGroups.length - 1].editingId;
    props.postEnforceCancelAsync(props.accountid, props.id, props.idid, props.editingGroups[0].enforceGroups[0].actId, [targetEId]);
    handleCloseRevisionDialog();
  };

  const revision = (actid: string) => (event: React.MouseEvent<HTMLElement>) => {
    props.setLoading(true);
    props.getEnforcegroupsAsync(actid, props.accountid);

    handleRevisionDialog(event);
  };

  // 廃止・失効の区分（ACT_STATE 1:廃止 2:失効 3:全部改正廃止）
  const getStatusName = (status: string[] | undefined) => {
    switch (status && status[0]) {
      case '1':
        return '廃止';
      case '2':
        return '失効';
      case '3':
        return '全部改正廃止';
      default:
        return '';
    }
  };

  // 失効のみ「失効日」、廃止・全部改正廃止は「廃止日」
  const getStatusDateName = (status: string[] | undefined) => {
    return status && status[0] === '2' ? '失効日：' : '廃止日：';
  };

  const getDateString = (s: string[] | undefined) => {
    if (!s || !s[0]) {
      return '';
    }
    return new Intl.DateTimeFormat('ja-JP-u-ca-japanese', {
      year: 'numeric',
      month: 'narrow',
      day: 'numeric',
      era: 'long'
    }).format(new Date(s[0]));
  };

  // 用語検索
  const [words, setWords] = React.useState(props.rconditions.words);
  // 他画面からの遷移で検索条件がリセットされた場合に入力欄も追従させる
  React.useEffect(() => {
    setWords(props.rconditions.words);
  }, [props.rconditions.words]);

  const searchWords = (w: string) => {
    const c: Conditions = JSON.parse(JSON.stringify(props.rconditions));
    c.words = w;
    // 条件が変わるので先頭ページに戻す
    c.page = 0;
    props.setLoading(true);
    props.searchRepealAsync(c, props.accountid);
  };

  const handleSearch = () => searchWords(words);

  const handleClear = () => {
    setWords('');
    searchWords('');
  };

  const handleKeyDownWords = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const createList = () => {
    const createAction = (r: Hit) => {
      return r.fields.historyicondisp[0] && r.fields.eventtype[0] === '3' ? (
        <>
          <Tooltip title="改正履歴">
            <IconButton sx={{
              color: (theme) => theme.palette.secondary.main,
              margin: 1,
            }} edge="end" aria-label="revision" onClick={revision(r.fields.actid[0])}>
              <ManageHistoryIcon />
            </IconButton>
          </Tooltip>
        </>
      ) : '';
    };

    return props.rsearchResultSet ? props.rsearchResultSet.hit.map((i, index) => {
      const v = i.fields;
      return (
        <div key={"" + index}>
          <ListItem secondaryAction={
            <>
              {createAction(i)}
            </>
          }>
            <ListItemButton onClick={() => actView(v.actid, v.revision)}>
              <ListItemIcon sx={{
                color: (theme) => theme.palette.primary.main,
              }}>
                <NoteIcon />
              </ListItemIcon>
              <ListItemText primary={v.title} secondary={
                <>
                  <Typography variant="subtitle1" component="span">番号：</Typography>
                  <Typography variant="body1" component="span">{v.actnum}</Typography>
                  <br />
                  <Typography variant="subtitle1" component="span">区分：</Typography>
                  <Typography variant="body1" component="span">{getStatusName(v.status)}</Typography>
                  <br />
                  <Typography variant="subtitle1" component="span">{getStatusDateName(v.status)}</Typography>
                  <Typography variant="body1" component="span">{getDateString(v.repealdate)}</Typography>
                  <br />
                </>
              } />
            </ListItemButton>
          </ListItem>
          <Divider />
        </div>
      )
    }) : '';
  };

  return (
    <>
      <Typography sx={{ margin: 2 }} variant="h5" gutterBottom>
        <BackupTable fontSize='large' color='primary' sx={{ marginBottom: 1, marginRight: 1, verticalAlign: 'middle' }} />
        廃止・失効された文書
      </Typography>
      <Paper sx={{
        minWidth: 275,
        margin: 2,
        padding: 1,
      }} elevation={3}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: 1 }}>
          <TextField
            id="repeal-words"
            label="用語"
            placeholder="題名・本文から検索します"
            value={words}
            size="small"
            fullWidth
            onChange={(event) => setWords(event.target.value)}
            onKeyDown={handleKeyDownWords}
          />
          <Button onClick={handleSearch} color="secondary" startIcon={<SearchIcon />} sx={{ flexShrink: 0 }}>
            検索
          </Button>
          <Button onClick={handleClear} color="primary" startIcon={<ClearAll />} sx={{ flexShrink: 0 }}>
            クリア
          </Button>
        </Box>
      </Paper>
      {props.rsearchResultSet ? createTablePagination() : ''}
      <Paper sx={{
        minWidth: 275,
        margin: 2,
        padding: 1,
        backgroundColor: (theme) => theme.palette.primary[50],
      }} elevation={3}>
        <List component="nav" aria-label="My editing acts">
          {createList()}
        </List>
      </Paper>
      {createTablePagination()}<br /><br /><br />
      <RevisionDialog
        open={openRevisionDialog}
        accountid={props.accountid}
        role={props.role}
        editingGroups={props.editingGroups}
        editingGroupsOrg={props.editingGroupsOrg}
        getEnforcegroups={props.getEnforcegroups}
        handleCommit={handleCommitRevisionDialog}
        handleClose={handleCloseRevisionDialog}
      />
    </>
  );
};